import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { Button } from '../components/ui/Button';
import { useGetEventsByCategory } from '../api/events';
import type { EventItem } from '../api/events';
import { FiCalendar, FiMapPin, FiArrowLeft, FiAlertCircle, FiInbox } from 'react-icons/fi';

export const Route = createFileRoute('/events/category/$filterName')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: location.pathname },
      });
    }
  },
  component: EventCategoryPage,
});

// ─── Filter label map ─────────────────────────────────────────────────────────

const FILTER_LABELS: Record<string, { title: string; subtitle: string }> = {
  all_events: { title: 'All Events', subtitle: 'Browse every event on Musicade' },
  recent: { title: 'Recent Events', subtitle: 'Latest happenings near you' },
  cancelled: { title: 'Cancelled Events', subtitle: 'Events that were cancelled' },
  collaborators: { title: 'Collaborating Events', subtitle: 'Events you are helping with' },
  likes: { title: 'Liked Events', subtitle: 'Events you have liked' },
  event_creator: { title: 'My Created Events', subtitle: 'Events you have organised' },
  ticket_bought: { title: 'Tickets Bought', subtitle: 'Events you have tickets for' },
  draft: { title: 'Draft Events', subtitle: 'Single events not yet published' },
  multi_event_draft: { title: 'Draft Multi-Events', subtitle: 'Multi-events not yet published' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '';
  // Event times are stored "as picked" (UTC-stamped), so read them back in UTC.
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

function getFirstImage(event: EventItem): string | null {
  const medias = event.medias ?? [];
  for (const m of medias) {
    const url = m.file_url ?? m.image ?? m.file ?? null;
    if (url) return url;
  }
  return null;
}

function getLocation(event: EventItem): string | null {
  if (event.venue) return event.venue;
  if (event.address) return event.address;
  const parts = [event.city, event.state, event.country].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

// ─── Event Card ───────────────────────────────────────────────────────────────

function EventCard({ event }: { event: EventItem }) {
  const cover = getFirstImage(event);
  const location = getLocation(event);
  const isMulti = event.event_structure_type === 'multi_event';
  const isDraft = event.is_draft_or_published === 'draft';
  const isExternal =
    event.event_ticketing_type === 'external' || event.event_structure_type === 'external';

  return (
    <Link to="/events/$eventId" params={{ eventId: String(event.id) }}>
      <div className="group relative bg-zinc-900 rounded-2xl overflow-hidden border border-white/5 hover:border-white/15 transition-all duration-200 hover:scale-[1.02] cursor-pointer">
        {/* Cover image */}
        <div className="relative w-full aspect-square bg-zinc-800 overflow-hidden">
          {cover ? (
            <img
              src={cover}
              alt={event.event_title ?? ''}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiCalendar size={32} className="text-zinc-700" />
            </div>
          )}
          <div className="absolute inset-0 bg-linear-to-b from-black/40 to-transparent" />
          {event.is_cancelled && (
            <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-500/90 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg">
              <FiAlertCircle size={11} />
              Cancelled
            </div>
          )}
          {/* Draft badge — top-right */}
          {isDraft && !event.is_cancelled && (
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-amber-500/30 backdrop-blur-sm text-amber-300 text-[11px] font-bold px-2.5 py-1 rounded-lg border border-amber-500/20">
              Draft
            </div>
          )}
          {isMulti && !event.is_cancelled && !isDraft && !isExternal && (
            <div className="absolute top-2 right-2 bg-blue-500/30 backdrop-blur-sm text-white/80 text-[11px] font-bold px-2.5 py-1 rounded-lg">
              Multi-Event
            </div>
          )}
        </div>
        {/* Info */}
        <div className="p-3 space-y-1.5">
          <p className="text-white text-[13px] font-bold leading-tight line-clamp-2">
            {event.event_title ?? 'Untitled Event'}
          </p>
          {location && (
            <p className="text-zinc-400 text-[12px] flex items-center gap-1 truncate">
              <FiMapPin size={11} className="shrink-0" />
              {location}
            </p>
          )}
          {event.start && (
            <p className="text-zinc-500 text-[11px] flex items-center gap-1">
              <FiCalendar size={11} className="shrink-0" />
              {formatDate(event.start)}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function CardSkeleton() {
  return (
    <div className="bg-zinc-900 rounded-2xl overflow-hidden animate-pulse">
      <div className="aspect-square bg-zinc-800" />
      <div className="p-3 space-y-2">
        <div className="h-3 bg-zinc-700 rounded w-4/5" />
        <div className="h-3 bg-zinc-800 rounded w-3/5" />
        <div className="h-3 bg-zinc-800 rounded w-2/5" />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 12;

function EventCategoryPage() {
  const { filterName } = Route.useParams();
  const [page, setPage] = useState(1);
  const [allEvents, setAllEvents] = useState<EventItem[]>([]);

  const meta = FILTER_LABELS[filterName] ?? {
    title: filterName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    subtitle: 'Browsing events',
  };

  // Reset on filter change
  useEffect(() => {
    setPage(1);
    setAllEvents([]);
  }, [filterName]);

  const { data, isLoading, isFetching, isError, refetch } = useGetEventsByCategory(
    filterName,
    page,
    PAGE_SIZE,
  );

  // Accumulate pages
  useEffect(() => {
    if (!data?.results) return;
    setAllEvents((prev) => (page === 1 ? data.results : [...prev, ...data.results]));
  }, [data, page]);

  const hasMore = Boolean(data?.next);
  const handleLoadMore = useCallback(() => {
    if (!isFetching) setPage((p) => p + 1);
  }, [isFetching]);

  const isFirstLoad = isLoading && page === 1;
  const isEmpty = !isFirstLoad && !isError && allEvents.length === 0 && !isFetching;

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mt-2 mb-8">
          <div className="flex items-center gap-3 mb-1">
            <Link
              to="/events"
              className="p-2 rounded-full hover:bg-white/5 transition-colors text-zinc-400 hover:text-white"
            >
              <FiArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white">{meta.title}</h1>
              <p className="text-zinc-500 text-sm">{meta.subtitle}</p>
            </div>
          </div>

          {/* Active filter pill */}
          <div className="ml-11 flex items-center gap-2 mt-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-[12px] font-medium">
              {meta.title}
            </span>
            {data?.count !== undefined && (
              <span className="text-zinc-600 text-[12px]">{data.count} events</span>
            )}
          </div>
        </div>

        {/* First load skeleton */}
        {isFirstLoad && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <FiAlertCircle size={40} className="text-zinc-600" />
            <p className="text-zinc-400 text-sm">Failed to load events.</p>
            <Button onClick={() => refetch()} variant="secondary" className="rounded-full px-6">
              Try Again
            </Button>
          </div>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
              <FiInbox size={28} className="text-zinc-600" />
            </div>
            <div>
              <p className="text-white text-base font-semibold">No events found</p>
              <p className="text-zinc-500 text-sm mt-1">
                There are no events in this category yet.
              </p>
            </div>
            <Link to="/events">
              <Button variant="secondary" className="rounded-full px-6">
                Back to Events
              </Button>
            </Link>
          </div>
        )}

        {/* Grid */}
        {!isFirstLoad && !isError && allEvents.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {allEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
              {/* Append skeleton rows while loading more */}
              {isFetching &&
                page > 1 &&
                Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={`sk-${i}`} />)}
            </div>

            {/* Load More */}
            {(hasMore || (isFetching && page > 1)) && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={isFetching}
                  className="px-8 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-white/8 text-zinc-300 text-[13px] font-medium transition-colors"
                >
                  {isFetching ? 'Loading…' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
